// Zustand vanilla store: { game, ui } + dispatch(cmd). The UI never mutates game state (docs/08 §2.6).
import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import { B } from '../content/balance';
import { applyCommand } from '../sim/commands';
import { createInitialState } from '../sim/meta';
import type { Command, EndingId, Expression, GameEvent, GameState } from '../sim/types';
import { loadLocal, pushBackup, saveLocal } from './save';

export type Screen = 'title' | 'machine' | 'web';
export type Stage = 'play' | 'intermission' | 'consumed' | 'bonus' | 'waking' | 'ending';
export type Panel = null | 'deck' | 'odds' | 'settings' | 'hand' | 'ledger' | 'reliquary' | 'codex';

export interface LogLine {
  id: number;
  kind: 'say' | 'tip' | 'print';
  key: string;
  params?: Record<string, string | number>;
  /** High Doom: something else speaks for a moment, in lowercase (docs/06 §4). */
  glitch?: boolean;
}

export interface UIState {
  screen: Screen;
  stage: Stage;
  busy: boolean;
  panel: Panel;
  debug: boolean;
  confirmOffer: boolean;
  confirmMolt: boolean;
  /** Values that tick up as animations land (docs/08 §6). null → show the true value. */
  displayedCoins: string | null;
  displayedSouls: number | null;
  log: LogLine[];
  say: { key: string; params?: Record<string, string | number>; expression?: Expression; id: number } | null;
  expression: Expression;
  selectedNode: string | null;
  toast: { text: string; id: number } | null;
  /** Hover preview for nudge arrows. */
  nudgeHover: { reel: number; dir: 1 | -1 } | null;
  eventLog: GameEvent[];
  /** An ending sequence playing over everything (docs/06 §7). */
  cinematic: { ending: EndingId; first: boolean } | null;
  /** Dolores on the payphone: the script being played. */
  call: string | null;
  /** The missing-person flyer is open. */
  flyer: boolean;
  /** Touch layout: which side panel is pulled out (docs/07 §1.3). */
  drawer: 'receipt' | 'status' | null;
  /** Gamepad: a pad is connected, and the reel its D-pad has picked. */
  padActive: boolean;
  padReel: number;
}

export interface Store {
  game: GameState;
  ui: UIState;
  dispatch(cmd: Command): boolean;
  setUI(patch: Partial<UIState>): void;
  replaceGame(game: GameState): void;
}

type Listener = (events: GameEvent[], cmd: Command, prev: GameState) => void;
const listeners = new Set<Listener>();
export function onEvents(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

let lineId = 1;
export const nextId = () => lineId++;

function initialUI(game: GameState): UIState {
  return {
    screen: 'title',
    stage: stageFor(game),
    busy: false,
    panel: null,
    debug: false,
    confirmOffer: false,
    confirmMolt: false,
    displayedCoins: null,
    displayedSouls: null,
    log: [],
    say: null,
    expression: 'neutral',
    selectedNode: null,
    toast: null,
    nudgeHover: null,
    eventLog: [],
    cinematic: null,
    call: null,
    flyer: false,
    drawer: null,
    padActive: false,
    padReel: 0,
  };
}

export function stageFor(game: GameState): Stage {
  const p = game.run?.phase;
  if (p === 'intermission') return 'intermission';
  if (p === 'consumed') return 'consumed';
  if (p === 'bonus') return 'bonus';
  if (p === 'ending' || p === 'remember') return 'ending';
  return 'play';
}

function newSeed(): string {
  const a = new Uint32Array(2);
  crypto.getRandomValues(a);
  return `${a[0]!.toString(36)}${a[1]!.toString(36)}`;
}

const AUTOSAVE_AFTER = new Set<Command['t']>(['PULL', 'COLLECT', 'NUDGE', 'PAY_NOW', 'CONTINUE', 'NEW_RUN', 'BUY_NODE', 'OFFER_SELF', 'DRAFT_PICK', 'DRAFT_SKIP', 'SHOP_BUY', 'BONUS_INPUT', 'SET_SETTINGS']);

let lastSave = 0;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let savesSinceBackup = 0;

export function saveNow(game: GameState): void {
  lastSave = Date.now();
  const text = saveLocal(game);
  if (++savesSinceBackup >= 10) {
    savesSinceBackup = 0;
    void pushBackup(text);
  }
}

function scheduleSave(get: () => Store): void {
  const since = Date.now() - lastSave;
  if (since >= B.AUTOSAVE_THROTTLE_MS) return saveNow(get().game);
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    saveNow(get().game);
  }, B.AUTOSAVE_THROTTLE_MS - since);
}

const isTest = typeof location !== 'undefined' && new URLSearchParams(location.search).has('test');
const loaded = typeof localStorage !== 'undefined' && !isTest ? loadLocal() : null;
const startGame = loaded ?? createInitialState(isTest ? 'test-seed' : newSeed());

export const store = createStore<Store>()((set, get) => ({
  game: startGame,
  ui: initialUI(startGame),
  dispatch(cmd) {
    const { game } = get();
    const r = applyCommand(game, cmd);
    if (r.error) {
      console.info(`command ${cmd.t} refused: ${r.error}`);
      for (const l of listeners) l([], cmd, game);
      return false;
    }
    const eventLog = [...get().ui.eventLog, ...r.events].slice(-200);
    set({ game: r.state, ui: { ...get().ui, eventLog } });
    for (const l of listeners) l(r.events, cmd, game);
    if (AUTOSAVE_AFTER.has(cmd.t) && !isTest) scheduleSave(get);
    return true;
  },
  setUI(patch) {
    set({ ui: { ...get().ui, ...patch } });
  },
  replaceGame(game) {
    set({ game, ui: { ...initialUI(game), screen: get().ui.screen } });
    saveNow(game);
  },
}));

export const dispatch = (cmd: Command) => store.getState().dispatch(cmd);
export const setUI = (patch: Partial<UIState>) => store.getState().setUI(patch);
export const getGame = () => store.getState().game;
export const getUI = () => store.getState().ui;

export function pushLog(line: Omit<LogLine, 'id'>): void {
  const log = [...getUI().log, { ...line, id: nextId() }].slice(-30);
  setUI({ log });
}

export function toast(text: string): void {
  setUI({ toast: { text, id: nextId() } });
}

export function useGame<T>(sel: (g: GameState) => T): T {
  return useStore(store, (s) => sel(s.game));
}
export function useUI<T>(sel: (u: UIState) => T): T {
  return useStore(store, (s) => sel(s.ui));
}

if (typeof window !== 'undefined' && !isTest) {
  const flush = () => saveNow(store.getState().game);
  window.addEventListener('beforeunload', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

export const TEST_MODE = isTest;
